/*
 * Copyright (c) 2023. Bluemate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import {ActivateHandle} from '../../../util/interfaces';
import {createErrorMessage, downloadFileURL} from '../../../util/utils';
import {exportProductCostsV2, importProductCostsV2} from '../../../util/requestSender';
import {downloadFile, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import ProductCostsUpload from './productCostsUpload';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: VoidFunction;
}

function ProductCostsUploadController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t, i18n} = useTranslation();
    const {onSuccess} = props;
    const [importFile, setImportFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setImportFile(null);
        setLoading(false);
    }

    function onDownloadTemplate() {
        let currentLanguage = i18n.language;
        // Fallback to nl for unexpected languages
        if (!['nl', 'en'].includes(currentLanguage)) currentLanguage = 'nl';
        const fileURL = `https://app.bluemate.nl/static/product-kosten-v2-template-${currentLanguage}.xlsx`;
        downloadFileURL(fileURL, 'product-kosten-import-template.xlsx');
    }

    function onDownloadProductCosts() {
        if (!account) return;
        setLoading(true);
        exportProductCostsV2({
            account_id: account.id,
            callback: (data: ResponseData, response: Response) => {
                if (data.status === 'success') {
                    response.blob().then((blob: Blob) => {
                        const header = response.headers.get('Content-Disposition');
                        let fileName = 'productkosten.xlsx';
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboard.productCostsUpload.exportFailedMsg',
                                    'Het exporteren van de data is niet gelukt.'
                                )
                        ),
                        t('salesDashboard.productCostsUpload.exportFailedTitle', 'Exporteren mislukt')
                    );
                }
            }
        });
    }

    function onChangeFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files) return;
        setImportFile(files[0]);
    }

    function onSubmit() {
        if (!importFile || !account) return;
        setLoading(true);
        importProductCostsV2({
            account_id: account.id,
            file: importFile,
            callback: (data: ResponseData) => {
                setLoading(false);
                onSuccess();
                if (data.success) {
                    const {row_errors} = data;
                    if (row_errors && Object.keys(row_errors).length) {
                        const errorContent = (
                            <label>
                                {t(
                                    'salesDashboard.productCostsUpload.partialSuccess',
                                    'Het importeren van de productkosten is deels gelukt, maar de volgende fouten zijn gevonden:'
                                )}
                                {Object.entries(row_errors).map(([row, error]: [string, string], idx: number) => (
                                    <span key={idx}>
                                        {t('salesDashboard.productCostsUpload.rowLabel', 'Regel')} {row}: {error}
                                        <br />
                                    </span>
                                ))}
                            </label>
                        );
                        showDialog(
                            errorContent,
                            t('salesDashboard.productCostsUpload.partialSuccessTitle', 'Importeren deels gelukt')
                        );
                    } else {
                        showSnackbar(
                            t(
                                'salesDashboard.productCostsUpload.importedSnackbar',
                                'De productkosten zijn geïmporteerd'
                            )
                        );
                        onClose();
                    }
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'salesDashboard.productCostsUpload.importFailedMsg',
                                    'Het importeren van de productkosten is niet gelukt.'
                                )
                        ),
                        t('salesDashboard.productCostsUpload.importFailedTitle', 'Importeren mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ProductCostsUpload
            onClose={onClose}
            onDownloadTemplate={onDownloadTemplate}
            onChangeFile={onChangeFile}
            onSubmit={onSubmit}
            onDownloadProductCosts={onDownloadProductCosts}
            importFile={importFile}
            loading={loading}
            active={active}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ProductCostsUploadController);
